/**
 * Copyright 2023 Design Barn Inc.
 */

'use client';

import { Button } from '@lottiefiles/ds-core';
import React, { useCallback, useEffect } from 'react';
import type { ControllerRenderProps, UseFormStateReturn } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';

import Input from '../input/input';

export interface ForgotPasswordFormInput {
  email: string;
}

export interface ForgotPasswordFormProps {
  errors?: {
    email?: string;
  };
  onSubmit: (data: ForgotPasswordFormInput) => Promise<void>;
  submitDisabled: boolean;
  submitLabel: string;
}

export default function ForgotPasswordForm({
  errors,
  onSubmit,
  submitDisabled,
  submitLabel,
}: ForgotPasswordFormProps): React.ReactElement {
  const {
    clearErrors,
    control,
    formState: { errors: formErrors, isValid },
    handleSubmit,
    setError,
    setFocus,
  } = useForm<ForgotPasswordFormInput>({
    defaultValues: {
      email: '',
    },
  });

  useEffect(() => {
    setFocus('email');
  }, [setFocus]);

  useEffect(() => {
    if (errors?.email || formErrors.email?.message) {
      setError('email', { message: errors?.email || formErrors.email?.message });
    } else {
      clearErrors('email');
    }
  }, [errors?.email, setError, clearErrors, formErrors.email?.message]);

  const renderEmailField = useCallback(
    ({
      field,
      formState,
    }: {
      field: ControllerRenderProps<ForgotPasswordFormInput, 'email'>;
      formState: UseFormStateReturn<ForgotPasswordFormInput>;
    }) => (
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...field}
        label="Email"
        placeholder="Email"
        type="email"
        errorText={formState.errors.email?.message}
      />
    ),
    [],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Controller
          name="email"
          control={control}
          render={renderEmailField}
          // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
          rules={{ required: 'Email is missing.' }}
        />
        <Button
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop, @typescript-eslint/no-empty-function
          onClick={(): void => {}}
          variant="primary"
          size="lg"
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: DS library extends a component that has the type property
          type="submit"
          disabled={submitDisabled || !isValid}
          className="w-full rounded-[12px] text-base font-normal mt-4 mb-[36px]"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
